import { type ReactiveController, type ReactiveControllerHost } from "lit";

import { AsyncDirective, type DirectiveResult, type PartInfo, directive } from "lit/async-directive.js";

const supportedLanguages = ["en", "nl"] as const;
export type Language = (typeof supportedLanguages)[number];

const storageKey = "language";

detectNL();

export type GetLeafKeys<TTranslations> = TTranslations extends object
  ? {
      [TKey in keyof TTranslations]: TTranslations[TKey] extends string | string[] | unknown[]
        ? `${TKey & string}`
        : `${TKey & string}.${GetLeafKeys<TTranslations[TKey]>}`;
    }[keyof TTranslations]
  : never;

export type TranslationsSchema<T> = T extends { [K in Language]: infer R } ? R : never;
export type TranslationKeys<T> = GetLeafKeys<TranslationsSchema<T>>;

export type TranslationLeaf = string | string[] | unknown[];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function init<T extends { [K in Language]: Record<string, any> }>(allTranslations: T) {
  type Keys = TranslationKeys<T>;

  for (const lang of supportedLanguages) {
    translations.set(lang, allTranslations[lang]);
  }

  function translate(key: Keys, params?: Record<string, string | object>): string {
    const lang = getLang();
    return getValue(lang, key, params);
  }

  function translateRaw(key: Keys, params?: Record<string, string | object>): TranslationLeaf {
    const lang = getLang();
    return getValueRaw(lang, key, params);
  }

  const translationDirective = directive(Translate) as <K extends Keys>(
    key: Keys,
    params?: Record<string, string | object> | undefined,
  ) => DirectiveResult<typeof Translate<K>>;

  return {
    t: translationDirective,
    translate,
    translateRaw,
    setLanguage(language: Language) {
      localStorage.setItem(storageKey, language);
      for (const listener of listeners) {
        listener.changeLanguage(language);
      }
      for (const listener of languageChangeListeners) {
        listener(language);
      }
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const translations: Map<Language, Record<string, any>> = new Map();

function substituteTokens(str: string, language: Language, params?: Record<string, string | object>): string {
  if (!params) return str;
  let out = str;
  for (const [p, val] of Object.entries(params)) {
    if (typeof val === "string") {
      out = out.replaceAll(`{${p}}`, val);
      continue;
    }
    if (
      typeof val === "object" &&
      val !== null &&
      "values" in val &&
      Array.isArray((val as { values: unknown }).values) &&
      typeof (val as { values: unknown[] }).values[0] === "string" &&
      typeof (val as { values: unknown[] }).values[1] === "object" &&
      (val as { values: unknown[] }).values[1] !== null
    ) {
      const valuesArray = (val as { values: unknown[] }).values as [string, object];
      const replacement = getValueRaw(language, valuesArray[0], valuesArray[1]);
      if (Array.isArray(replacement)) {
        out = out.replaceAll(
          `{${p}}`,
          replacement.map((v) => (typeof v === "string" ? v : JSON.stringify(v))).join(", "),
        );
      } else {
        out = out.replaceAll(`{${p}}`, String(replacement));
      }
    } else {
      out = out.replaceAll(`{${p}}`, String(val));
    }
  }
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getNestedValue(obj: Record<string, any>, path: string[]): TranslationLeaf | undefined {
  let current: unknown = obj;
  for (const key of path) {
    if (current === undefined || current === null) return undefined;
    current = (current as Record<string, unknown>)[key as string];
  }
  if (typeof current === "string") return current as string;
  if (Array.isArray(current)) return current as unknown[];
  return undefined;
}

function getValueRaw<K extends string>(language: Language, key: K, params?: object): TranslationLeaf {
  const tr = translations.get(language) ?? {};
  const path = key.split(".");
  const value = getNestedValue(tr, path);
  if (value === undefined) return "[" + key + "]";
  if (Array.isArray(value)) {
    return value.map((v) =>
      typeof v === "string" ? substituteTokens(v, language, params as Record<string, string | object>) : v,
    ) as unknown[];
  }
  return substituteTokens(value as string, language, params as Record<string, string | object>);
}

function getValue<K extends string>(language: Language, key: K, params?: object): string {
  const raw = getValueRaw(language, key, params);
  if (Array.isArray(raw)) {
    return raw.map((v) => (typeof v === "string" ? v : JSON.stringify(v))).join(", ");
  }
  return raw as string;
}

const listeners = new Set<Translate<string>>();
const languageChangeListeners = new Set<(language: Language) => void>();

class Translate<K extends string> extends AsyncDirective {
  #_key?: K;
  #_params?: Record<string, string | object>;
  constructor(partInfo: PartInfo) {
    super(partInfo);
    listeners.add(this);
  }
  changeLanguage(newLang: Language) {
    if (this.#_key != undefined) {
      this.setValue(getValueRaw<K>(newLang, this.#_key, this.#_params));
    }
  }
  override reconnected(): void {
    listeners.add(this);
  }
  override disconnected(): void {
    listeners.delete(this);
  }
  render(key: K, params?: Record<string, string | object>) {
    this.#_key = key;
    this.#_params = params;
    const lang = getLang();
    if (translations.has(lang)) {
      return getValueRaw(lang, key, params);
    }
    return "[" + key + "]";
  }
}

function getLang(): Language {
  const lang = (localStorage.getItem("language") ?? navigator.language) as Language;

  return supportedLanguages.includes(lang) ? lang : "en";
}

export class LanguageController implements ReactiveController {
  private unsubscribe?: () => void;

  constructor(private host: ReactiveControllerHost) {
    this.host.addController(this);
  }

  get language(): Language {
    return getLang();
  }

  setLanguage(language: Language) {
    localStorage.setItem(storageKey, language);
    for (const listener of listeners) {
      listener.changeLanguage(language);
    }
    for (const listener of languageChangeListeners) {
      listener(language);
    }
  }

  hostConnected() {
    this.unsubscribe = this.onLanguageChange(() => {
      this.host.requestUpdate();
    });
  }

  hostDisconnected() {
    this.unsubscribe?.();
  }

  private onLanguageChange(callback: (language: Language) => void): () => void {
    languageChangeListeners.add(callback);
    return () => languageChangeListeners.delete(callback);
  }
}

async function detectNL() {
  try {
    if (!localStorage.getItem("language")) {
      const res = await fetch("https://ipapi.co/json/");
      const data = (await res.json()) as { country: string };

      if (data.country === "NL" || data.country === "IN") {
        localStorage.setItem("language", "nl");
      }
    }
  } catch {
    console.error("Error loading geolocation language");
  }
}
